import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ChevronDown, Snowflake, Wallet } from "lucide-react";
import { getTelegramUser, openTelegramLink } from "../../../lib/tg";
import { useVisibleRefresh } from "../../../lib/useVisibleRefresh";
import { sponsorApi } from "../lib/api";

type SponsorBalance = {
  balance: string;
  frozen?: string | null;
};

type FinanceOperation = {
  id: string;
  kind?: string;
  title: string;
  amount_rub: string;
  amount_usdt?: string | null;
  status: string;
  method?: string | null;
  created_at: string;
  details?: string | null;
};

type Props = {
  onBack: () => void;
};

type TopupMethod = "invoice" | "support";

type TopupRate = {
  rate_rub_usdt: string;
  fetched_at: string;
  source: string;
};

type TopupInputSide = "rub" | "usdt";

const TOPUP_RATE_TTL_MS = 15 * 60 * 1000;
const TOPUP_USDT_COMMISSION_PERCENT = "3";
const TOPUP_USDT_COMMISSION_RATE = 0.03;
const TOPUP_SUPPORT_LINK = "https://t.me/m/HAbAgCy6NWRi";

export function SponsorBalance({ onBack: _onBack }: Props) {
  const [balance, setBalance] = useState<SponsorBalance | null>(null);
  const [operations, setOperations] = useState<FinanceOperation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [topupMode, setTopupMode] = useState(false);
  const [topupMethod, setTopupMethod] = useState<TopupMethod | null>(null);
  const [amountRub, setAmountRub] = useState("");
  const [amountUsdt, setAmountUsdt] = useState("");
  const [activeTopupInput, setActiveTopupInput] =
    useState<TopupInputSide>("rub");
  const [topupRate, setTopupRate] = useState<TopupRate | null>(null);
  const [topupRateLoadedAt, setTopupRateLoadedAt] = useState<number | null>(
    null,
  );
  const [rateLoading, setRateLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const mountedRef = useRef(false);
  const balanceRequestRef = useRef<ReturnType<
    typeof sponsorApi.getSponsorBalance
  > | null>(null);

  const requestBalance = useCallback(async () => {
    if (balanceRequestRef.current) return await balanceRequestRef.current;
    const request = sponsorApi.getSponsorBalance();
    balanceRequestRef.current = request;
    try {
      return await request;
    } finally {
      if (balanceRequestRef.current === request) {
        balanceRequestRef.current = null;
      }
    }
  }, []);

  const refreshBalance = useCallback(
    async ({ reportError = false }: { reportError?: boolean } = {}) => {
      try {
        const response = await requestBalance();
        if (!mountedRef.current) return false;
        if (response.error || !response.data) {
          if (reportError) setError("не удалось загрузить баланс");
          return false;
        }
        setBalance(response.data as SponsorBalance);
        setError(null);
        return true;
      } catch {
        if (mountedRef.current && reportError) {
          setError("ошибка сети");
        }
        return false;
      }
    },
    [requestBalance],
  );

  const refreshOperations = useCallback(async () => {
    try {
      const response = await sponsorApi.listOperations();
      if (!mountedRef.current || response.error || !response.data) return;
      setOperations(response.data as FinanceOperation[]);
    } catch {
      // История не должна скрывать актуальный баланс.
    }
  }, []);

  const load = useCallback(async () => {
    if (!mountedRef.current) return;
    setLoading(true);
    setError(null);
    void refreshOperations();
    await refreshBalance({ reportError: true });
    if (mountedRef.current) setLoading(false);
  }, [refreshBalance, refreshOperations]);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  useVisibleRefresh(refreshBalance);

  useEffect(() => {
    if (!topupMode || !topupMethod) return undefined;
    void loadTopupRate();
    const intervalId = window.setInterval(() => {
      void loadTopupRate({ force: true });
    }, TOPUP_RATE_TTL_MS);
    return () => window.clearInterval(intervalId);
  }, [topupMode, topupMethod]);

  async function loadTopupRate(options?: { force?: boolean }) {
    const now = Date.now();
    if (
      !options?.force &&
      topupRate &&
      topupRateLoadedAt &&
      now - topupRateLoadedAt < TOPUP_RATE_TTL_MS
    ) {
      return;
    }
    if (rateLoading) return;
    setRateLoading(true);
    try {
      const r = await sponsorApi.getTopupRate();
      if (r.error) {
        setFormError("не удалось обновить курс");
        return;
      }
      const nextRate = r.data as TopupRate;
      setTopupRate(nextRate);
      setTopupRateLoadedAt(Date.now());
      const rate = parseMoney(nextRate.rate_rub_usdt);
      if (activeTopupInput === "usdt") {
        setAmountRub(
          calculateTopupRubToReceive(parseAmountInput(amountUsdt), rate),
        );
      } else {
        setAmountUsdt(
          calculateTopupUsdtToSend(parseAmountInput(amountRub), rate),
        );
      }
    } catch {
      setFormError("ошибка загрузки курса");
    } finally {
      setRateLoading(false);
    }
  }

  async function handleTopUp() {
    setFormError(null);
    const amount = topupRubAmountForSubmit();
    if (amount === null || amount <= 0) {
      setFormError("сумма должна быть больше нуля");
      return;
    }
    const normalizedAmount = formatInputAmount(amount, 2);

    setSubmitting(true);
    try {
      const r = await sponsorApi.createInvoice({
        amount: normalizedAmount,
      });
      if (r.error) {
        const detail = (r.error as { detail?: string }).detail;
        setFormError(detail ?? "ошибка при создании счёта");
        return;
      }
      const invoice = r.data as { pay_url: string };
      if (invoice.pay_url) {
        openTelegramLink(invoice.pay_url);
        setAmountRub("");
        setTopupMethod(null);
      } else {
        setFormError("не удалось получить ссылку на оплату");
      }
    } catch {
      setFormError("ошибка сети");
    } finally {
      setSubmitting(false);
    }
  }

  function openTopupMode() {
    setTopupMode(true);
    setTopupMethod(null);
    setAmountRub("");
    setAmountUsdt("");
    setActiveTopupInput("rub");
    setFormError(null);
  }

  function closeTopupMode() {
    setTopupMode(false);
    setTopupMethod(null);
    setAmountRub("");
    setAmountUsdt("");
    setActiveTopupInput("rub");
    setFormError(null);
  }

  function selectTopupMethod(method: TopupMethod) {
    setTopupMethod(method);
    setAmountRub("");
    setAmountUsdt("");
    setActiveTopupInput("rub");
    setFormError(null);
    void loadTopupRate();
  }

  function handleTopupRubChange(value: string) {
    setActiveTopupInput("rub");
    setAmountRub(value);
    setAmountUsdt(
      calculateTopupUsdtToSend(parseAmountInput(value), topupRateRub()),
    );
  }

  function handleTopupUsdtChange(value: string) {
    setActiveTopupInput("usdt");
    const nextValue = normalizeUsdtInput(value);
    setAmountUsdt(nextValue);
    setAmountRub(
      calculateTopupRubToReceive(parseAmountInput(nextValue), topupRateRub()),
    );
  }

  function topupRateRub(): number | null {
    return parseMoney(topupRate?.rate_rub_usdt);
  }

  function topupHasAmount(): boolean {
    return (
      (parseAmountInput(amountRub) ?? 0) > 0 ||
      (parseAmountInput(amountUsdt) ?? 0) > 0
    );
  }

  function topupRubAmountForSubmit(): number | null {
    const rub = parseAmountInput(amountRub);
    if (rub !== null && rub > 0) return rub;
    const usdt = parseAmountInput(amountUsdt);
    const rate = topupRateRub();
    if (usdt === null || usdt <= 0 || rate === null || rate <= 0) return null;
    return usdt * rate;
  }

  function handleTopupAction() {
    if (topupMethod === "invoice") {
      void handleTopUp();
      return;
    }
    if (topupMethod === "support") {
      openTelegramLink(TOPUP_SUPPORT_LINK);
    }
  }

  if (loading) {
    return (
      <div className="tp-screen" data-testid="loading" aria-hidden="true" />
    );
  }

  if (error) {
    return (
      <div className="tp-screen">
        <p className="tp-alert" data-testid="error">
          {error}
        </p>
        <button className="tp-button-primary" onClick={load}>
          повторить
        </button>
      </div>
    );
  }

  if (topupMode) {
    const currentRate = topupRateRub();
    const courseText = currentRate
      ? `${formatRubNumber(currentRate)} USDT`
      : "—";

    return (
      <div className="tp-screen tp-withdraw-screen">
        <TopupHeader title="пополнение средств" onBack={closeTopupMode} />

        <div className="tp-withdraw-methods">
          <button
            type="button"
            className="tp-withdraw-method"
            data-active={topupMethod === "invoice"}
            disabled={submitting}
            onClick={() => selectTopupMethod("invoice")}
          >
            счёт cryptobot, комиссия {TOPUP_USDT_COMMISSION_PERCENT}%
          </button>
          <button
            type="button"
            className="tp-withdraw-method"
            data-active={topupMethod === "support"}
            onClick={() => selectTopupMethod("support")}
          >
            через поддержку сервиса
          </button>
        </div>

        {topupMethod ? (
          <div className="tp-withdraw-conversion">
            <div className="tp-withdraw-refresh-row">
              <button
                type="button"
                disabled={rateLoading}
                onClick={() => loadTopupRate({ force: true })}
              >
                обновить
              </button>
            </div>
            <div className="tp-withdraw-conversion-card">
              <div className="tp-withdraw-conversion-row">
                <div className="tp-withdraw-currency-block">
                  <span className="tp-withdraw-row-caption">
                    вы отправляете
                  </span>
                  <div className="tp-withdraw-currency-label">
                    <UsdtIcon />
                    <span>USDT</span>
                  </div>
                </div>
                <div className="tp-withdraw-amount-column">
                  <div className="tp-withdraw-inline-balance">
                    <span>
                      баланс: {formatRubAmount(balance?.balance ?? "0")} ₽
                    </span>
                  </div>
                  <div className="tp-withdraw-balance-value">
                    <input
                      aria-label="сумма usdt"
                      className="tp-withdraw-amount-input"
                      value={amountUsdt}
                      onChange={(e) => handleTopupUsdtChange(e.target.value)}
                      inputMode="decimal"
                      min="0"
                      placeholder="0"
                      type="text"
                    />
                  </div>
                </div>
              </div>
              <div className="tp-withdraw-divider" />
              <div className="tp-withdraw-conversion-row">
                <div className="tp-withdraw-currency-block">
                  <span className="tp-withdraw-row-caption">вы получаете</span>
                  <div className="tp-withdraw-currency-label">
                    <RubIcon />
                    <span>RUB</span>
                  </div>
                </div>
                <div className="tp-withdraw-amount-column">
                  <div className="tp-withdraw-balance-value">
                    <input
                      aria-label="сумма rub"
                      className="tp-withdraw-amount-input"
                      value={amountRub}
                      onChange={(e) => handleTopupRubChange(e.target.value)}
                      inputMode="decimal"
                      min="0"
                      placeholder="0"
                      type="text"
                    />
                  </div>
                </div>
              </div>
              <div className="tp-withdraw-divider" />
              <div className="tp-withdraw-course">курс: {courseText}</div>
            </div>
            {formError ? (
              <p className="text-xs text-red-500" data-testid="form-error">
                {formError}
              </p>
            ) : null}
            {topupHasAmount() ? (
              <button
                type="button"
                className="tp-button-primary tp-withdraw-request-button w-full"
                disabled={submitting}
                onClick={handleTopupAction}
              >
                {submitting ? "создаём..." : "пополнить"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="tp-screen tp-balance-screen tp-sponsor-balance-screen">
      <ProfileBlock />

      {balance && (
        <div className="tp-balance-card">
          <div className="tp-balance-row">
            <div className="tp-balance-row-label">
              <Wallet size={18} strokeWidth={2} />
              <span>основной баланс</span>
            </div>
            <span className="tp-balance-value" data-testid="balance-available">
              {formatRubAmount(balance.balance)} ₽
            </span>
          </div>
          <div className="tp-balance-divider" />
          <div className="tp-balance-row">
            <div className="tp-balance-row-label">
              <Snowflake size={18} strokeWidth={2} />
              <span>заморожено</span>
            </div>
            <span className="tp-balance-value" data-testid="balance-frozen">
              {formatRubAmount(balance.frozen ?? "0")} ₽
            </span>
          </div>
        </div>
      )}

      <button
        className="tp-button-primary tp-balance-withdraw-button"
        onClick={openTopupMode}
      >
        <span>пополнить баланс</span>
      </button>

      <OperationsList operations={operations} />
    </div>
  );
}

function ProfileBlock() {
  const user = getTelegramUser();
  const name =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    (user?.username ? `@${user.username}` : "") ||
    (user?.id ? `id ${user.id}` : "") ||
    "пользователь";
  const initials = name.trim().slice(0, 1).toUpperCase() || "T";
  return (
    <div className="tp-balance-profile-card">
      {user?.photo_url ? (
        <img
          src={user.photo_url}
          alt=""
          className="tp-balance-avatar"
          draggable={false}
        />
      ) : (
        <div className="tp-avatar-fallback-badge tp-balance-avatar">
          {initials}
        </div>
      )}
      <div className="tp-balance-profile-name">{name}</div>
    </div>
  );
}

function TopupHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="tp-withdraw-header">
      <button type="button" aria-label="назад" onClick={onBack}>
        <ArrowLeft size={20} strokeWidth={2.2} />
      </button>
      <h1>{title}</h1>
    </div>
  );
}

function OperationsList({ operations }: { operations: FinanceOperation[] }) {
  const [open, setOpen] = useState(false);
  const groups = groupOperationsByDay(operations);

  return (
    <section className="tp-balance-history-card" data-open={open}>
      <button
        type="button"
        className="tp-balance-history-head"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <ClockHour4Icon />
        <span>история транзакций</span>
        <ChevronDown
          className="tp-balance-history-chevron"
          aria-hidden="true"
        />
      </button>
      {open ? (
        <div className="tp-balance-history-panel">
          {operations.length === 0 ? (
            <p className="tp-balance-empty-text">операций пока нет</p>
          ) : (
            <div className="tp-balance-history-groups">
              {groups.map((group) => (
                <div key={group.key} className="tp-balance-history-group">
                  <div className="tp-balance-history-date">{group.label}</div>
                  <ul className="tp-balance-history-list">
                    {group.items.map((op) => (
                      <OperationRow key={op.id} operation={op} />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}

function OperationRow({ operation }: { operation: FinanceOperation }) {
  const isModeration = operation.status === "pending";
  const course = formatOperationCourse(operation);
  return (
    <li className="tp-balance-operation-row">
      <div className="tp-balance-operation-icon-wrap">
        <OperationArrowIcon />
        {isModeration ? (
          <span className="tp-balance-operation-lock" aria-hidden="true">
            <LockBadgeIcon />
          </span>
        ) : null}
      </div>
      <div className="tp-balance-operation-main">
        <div className="tp-balance-operation-title">
          {operationTitle(operation)}
        </div>
        <div className="tp-balance-operation-time">
          {formatOperationDateTime(operation.created_at)}
        </div>
      </div>
      <div className="tp-balance-operation-side">
        <div className="tp-balance-operation-amount">
          {formatOperationAmount(operation)}
        </div>
        {course ? (
          <div className="tp-balance-operation-course">курс {course}</div>
        ) : null}
      </div>
    </li>
  );
}

function groupOperationsByDay(operations: FinanceOperation[]) {
  const groups = new Map<
    string,
    { key: string; label: string; items: FinanceOperation[] }
  >();
  for (const operation of operations) {
    const date = new Date(operation.created_at);
    const key = Number.isNaN(date.getTime())
      ? operation.created_at.slice(0, 10)
      : date.toISOString().slice(0, 10);
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(operation);
      continue;
    }
    groups.set(key, {
      key,
      label: formatOperationDay(operation.created_at),
      items: [operation],
    });
  }
  return Array.from(groups.values());
}

function operationTitle(operation: FinanceOperation): string {
  if (operation.status === "pending") return "на модерации";
  if (operation.status === "rejected" || operation.status === "cancelled") {
    return "отклонено";
  }
  const mapped = operationKindLabel(operation.kind ?? operation.title);
  if (mapped) return mapped;
  if (
    operation.kind === "withdrawal" ||
    operation.id.startsWith("withdrawal-")
  ) {
    return "пополнение средств";
  }
  return operation.title || operation.status;
}

function operationKindLabel(value: string | null | undefined): string | null {
  switch (value?.toLowerCase()) {
    case "topup":
    case "payout":
    case "withdrawal":
    case "вывод":
      return "пополнение средств";
    case "adjustment":
      return "изменение баланса";
    default:
      return null;
  }
}

function formatOperationDay(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
  });
}

function formatOperationDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${formatOperationDay(value)} ${date.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

function formatOperationAmount(operation: FinanceOperation): string {
  const rub = parseMoney(operation.amount_rub);
  const rubText =
    rub === null
      ? String(operation.amount_rub || "0")
      : formatRubNumber(Math.abs(rub));
  const usdt = parseMoney(operation.amount_usdt);
  const usdtText =
    usdt !== null ? ` = ${formatMoney(Math.abs(usdt))} USDT` : "";
  return `${rubText} ₽${usdtText}`;
}

function formatRubAmount(value: string | number | null | undefined): string {
  const parsed = parseMoney(value);
  if (parsed === null) return "0.0";
  return formatRubNumber(parsed);
}

function formatRubNumber(value: number): string {
  return formatMoney(truncateToTenths(value), 1, 1);
}

function formatOperationCourse(operation: FinanceOperation): string | null {
  const rub = parseMoney(operation.amount_rub);
  const usdt = parseMoney(operation.amount_usdt);
  if (rub === null || usdt === null || usdt === 0) return null;
  return formatRubNumber(Math.abs(rub) / Math.abs(usdt));
}

function parseMoney(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function parseAmountInput(
  value: string | number | null | undefined,
): number | null {
  if (value === null || value === undefined || value === "") return null;
  const normalized = String(value).replace(/\s/g, "").replace(",", ".").trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function calculateTopupUsdtToSend(
  rubToReceive: number | null,
  rateRubUsdt: number | null,
): string {
  if (
    rubToReceive === null ||
    rateRubUsdt === null ||
    rubToReceive <= 0 ||
    rateRubUsdt <= 0
  ) {
    return "";
  }
  return formatInputAmount(
    (rubToReceive * (1 + TOPUP_USDT_COMMISSION_RATE)) / rateRubUsdt,
    2,
  );
}

function calculateTopupRubToReceive(
  usdtToSend: number | null,
  rateRubUsdt: number | null,
): string {
  if (
    usdtToSend === null ||
    rateRubUsdt === null ||
    usdtToSend <= 0 ||
    rateRubUsdt <= 0
  ) {
    return "";
  }
  return formatInputAmount(
    (usdtToSend * rateRubUsdt) / (1 + TOPUP_USDT_COMMISSION_RATE),
    2,
  );
}

function normalizeUsdtInput(value: string): string {
  const normalized = value.replace(/\s/g, "").replace(",", ".");
  if (!normalized) return "";
  const match = normalized.match(/^(\d*)(?:\.(\d*))?/);
  if (!match) return "";
  const whole = match[1] || (normalized.startsWith(".") ? "0" : "");
  const fraction = match[2];
  if (fraction !== undefined) return `${whole}.${fraction.slice(0, 2)}`;
  return whole;
}

function formatInputAmount(
  value: number,
  maximumFractionDigits: number,
): string {
  return new Intl.NumberFormat("en-US", {
    useGrouping: false,
    maximumFractionDigits,
  }).format(value);
}

function formatMoney(
  value: number,
  maximumFractionDigits = 2,
  minimumFractionDigits = 0,
): string {
  return new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits,
    minimumFractionDigits,
  })
    .format(value)
    .replace(/[\s\u00a0\u202f]/g, ".");
}

function truncateToTenths(value: number): number {
  const truncated = Math.trunc(value * 10) / 10;
  return Object.is(truncated, -0) ? 0 : truncated;
}

function RubIcon() {
  return (
    <span className="tp-withdraw-currency-icon" aria-hidden="true">
      ₽
    </span>
  );
}

function UsdtIcon() {
  return (
    <span className="tp-withdraw-currency-icon" aria-hidden="true">
      ₮
    </span>
  );
}

function ClockHour4Icon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
      <path d="M12 12l3 2" />
      <path d="M12 7v5" />
    </svg>
  );
}

function OperationArrowIcon() {
  return (
    <svg
      width="42"
      height="42"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="32" cy="32" r="24" stroke="currentColor" strokeWidth="3" />
      <path
        d="M32 18V38"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M24 31L32 39L40 31"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LockBadgeIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}
